import React, { useContext, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import fileIcon from '../../../../../../../../../assets/img/file_icon.png';
import greyBin from '../../../../../../../../../assets/img/bin-grey.svg';
import uploadStart from '../../../../../../../../../assets/img/templateswizard/upload_start.png';

import TemplatesWizardContext from '../../../TemplatesWizardContext';
import Footer from '../../Footer/Footer';

import styles from './UploadFile.module.css';
import { TEMPLATES_WIZARD_STEP_KEYS } from '../../../../../helpers/constants';
import useLazyQuery from '../../../../../../../../hooks/useLazyQuery';
import { GET_MEDIA_UPLOAD_SAS } from '../../../../../../../../../graphql/Survey';

import isLocalhost from '../../../../../../../../../helpers/isLocalhost';

const formatFileSize = (bytes, decimals = 2) => {
  if (!Number.isFinite(bytes) || bytes < 0) return '0 B';
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / 1024 ** i;

  return `${value.toFixed(decimals).replace(/\.?0+$/, '')} ${units[i]}`;
};

const DropZoneText = ({
  isDragActive,
  uploading: inlineUploading,
  strings
}) => {
  if (inlineUploading) {
    return (
      <div className={styles.dropContainer}>{strings.UPLOAD_PLEASE_WAIT}</div>
    );
  }

  return (
    <div
      className={
        isDragActive
          ? `${styles.dropContainer} ${styles.dragActive}`
          : styles.dropContainer
      }
    >
      <img src={uploadStart} className={styles.uploadIcon} alt="Upload icon" />
      <div className={styles.title1}>{strings.DROP_YOUR_DOCUMENT_HERE}</div>
      <div className={styles.title2}>
        {strings.OR_BROWSE_FILES_FROM_YOUR_COMPUTER}
      </div>
      <div className={styles.fileExtensionsContainer}>
        <div>PDF</div>
        <div>TXT</div>
      </div>
    </div>
  );
};

export default () => {
  const {
    currentStep,
    goToNextStep,
    goToPreviousStep,
    strings,
    setStepValue
  } = useContext(TemplatesWizardContext);

  const [uploading, setUploading] = useState(false);

  const getMediaUploadSAS = useLazyQuery(GET_MEDIA_UPLOAD_SAS);

  const isDisabledNext = () => {
    if (!currentStep.value || !currentStep.value.fileName || uploading) {
      return true;
    }
    return false;
  };

  const onFileSelect = async acceptedFiles => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;

    if (isLocalhost()) {
      setUploading(true);
      setStepValue(TEMPLATES_WIZARD_STEP_KEYS.UPLOAD_FILE, {
        fileName: 'file_to_parse.pdf',
        fileSize: '12.5 KB',
        originalFileName: 'file_to_parse.pdf'
      });
      setUploading(false);
    } else {
      const origFilename = acceptedFiles[0].name;

      const {
        data: {
          mediaUploadSAS: { uri: sasURI, filename: uploadedFileName } = {}
        } = {}
      } = await getMediaUploadSAS({
        variables: {
          fileName: origFilename
        }
      });

      if (!sasURI) {
        return;
      }

      const xhr = new XMLHttpRequest();

      xhr.onload = () => {
        if (xhr.status === 201) {
          const fileSizeBytes = acceptedFiles[0].size;

          setStepValue(TEMPLATES_WIZARD_STEP_KEYS.UPLOAD_FILE, {
            originalFileName: origFilename,
            fileName: uploadedFileName,
            fileSize: formatFileSize(fileSizeBytes)
          });
        }
      };

      xhr.open('PUT', sasURI, true);
      xhr.setRequestHeader('Content-Type', acceptedFiles[0].type);
      xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');

      try {
        xhr.send(acceptedFiles[0]);
      } catch (err) {
        console.log(err);
      }
    }
  };

  const onFileRemoveClick = () => {
    setStepValue(TEMPLATES_WIZARD_STEP_KEYS.UPLOAD_FILE, null);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: 'text/plain, application/pdf',
    multiple: false,
    onDrop: onFileSelect
  });

  return (
    <div className={styles.container}>
      <div className={styles.title}>{strings.UPLOAD_YOUR_DOCUMENT}</div>
      <div className={styles.textContainer}>
        {currentStep && currentStep.value && currentStep.value.fileName ? (
          <div className={styles.uploadedFileContainer}>
            <img src={fileIcon} className={styles.fileIcon} alt="File icon" />
            <div className={styles.fileInformation}>
              <div className={styles.fileName}>
                {currentStep.value.originalFileName}
              </div>
              <div className={styles.fileSize}>
                {currentStep.value.fileSize}
              </div>
            </div>
            <img
              src={greyBin}
              className={styles.removeUploadedFile}
              alt="File remove icon"
              role="presentation"
              onClick={() => onFileRemoveClick()}
            />
          </div>
        ) : (
          <div className={styles.dropZone}>
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              <DropZoneText
                isDragActive={isDragActive}
                uploading={uploading}
                strings={strings} // Passed strings down explicitly
              />
            </div>
          </div>
        )}

        <Footer
          onNextClick={goToNextStep}
          onPreviousClick={goToPreviousStep}
          nextLabel={strings.NEXT}
          previousLabel={strings.PREVIOUS}
          disabledNext={isDisabledNext()}
        />
      </div>
    </div>
  );
};
